/**
 * FAQ pairs derived from an article's own markdown.
 *
 * Answer engines quote question-shaped passages. Rather than bolt boilerplate
 * Q&A onto every post, this reads the article's real section headings: any
 * heading already phrased as a question (or trivially convertible) becomes a
 * question, and the first substantive paragraph beneath it becomes the answer.
 * A post with no qualifying sections returns [] and renders no FAQ block.
 */

export interface DerivedFaq {
  q: string;
  a: string;
}

const QUESTION_WORDS =
  /^(what|which|how|why|when|where|who|is|are|do|does|can|should|will)\b/i;

function stripMarkdown(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function toQuestion(heading: string): string | null {
  const clean = stripMarkdown(heading);
  if (!clean || clean.length > 95) return null;
  if (clean.endsWith("?")) return clean;
  if (QUESTION_WORDS.test(clean)) return `${clean.replace(/[.:]$/, "")}?`;
  return null;
}

/** Extracts up to `limit` question/answer pairs from a markdown article. */
export function deriveArticleFaqs(markdown: string, limit = 5): DerivedFaq[] {
  if (!markdown) return [];
  const lines = markdown.split("\n");
  const faqs: DerivedFaq[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const match = /^(#{2,4})\s+(.*)$/.exec(lines[i]!);
    if (!match) continue;
    const question = toQuestion(match[2]!);
    if (!question || seen.has(question.toLowerCase())) continue;

    // Collect prose until the next heading; skip tables and list scaffolding so
    // the answer reads as a sentence rather than a fragment of markup.
    const body: string[] = [];
    for (let j = i + 1; j < lines.length && body.join(" ").length < 320; j++) {
      const line = lines[j]!;
      if (/^#{1,6}\s/.test(line)) break;
      const text = stripMarkdown(line.replace(/^[-*+]\s+/, ""));
      if (!text || text.startsWith("|") || /^-{3,}$/.test(text)) continue;
      body.push(text);
    }
    const answer = body.join(" ").trim();
    if (answer.length < 60) continue;

    seen.add(question.toLowerCase());
    faqs.push({ q: question, a: answer.length > 340 ? `${answer.slice(0, 339).replace(/\s+\S*$/, "")}…` : answer });
    if (faqs.length >= limit) break;
  }

  return faqs;
}

/**
 * BLUF sentence for an article: the excerpt is written as a direct answer, so
 * it is used verbatim when present and falls back to the article's opening
 * prose otherwise.
 */
export function articleBluf(excerpt: string | null | undefined, markdown: string): string | null {
  const clean = (excerpt ?? "").trim();
  if (clean.length >= 60) return clean;
  for (const line of markdown.split("\n")) {
    if (/^#{1,6}\s/.test(line) || !line.trim()) continue;
    const text = stripMarkdown(line);
    if (text.length >= 80) {
      return text.length > 320 ? `${text.slice(0, 319).replace(/\s+\S*$/, "")}…` : text;
    }
  }
  return clean || null;
}
